/**
 * Pantalla de **detalle de talonario por trabajador**. Sirve
 * `ROUTES.talonarioDetail` (`/talonarios/detalle/:worker`). Equivale a
 * `talonario_orders.html` del SSR.
 *
 * Exige `manage_talonarios` (vía `RequireManageTalonarios`). Carga el grid del
 * rango ACTUAL (`useTalonarioGrid`) y permite **marcar** (`useMarkNumber`) y
 * **desmarcar** (`useUnmarkNumber`) números; ambas mutaciones invalidan los
 * talonarios (la lista y el grid se refrescan). El error de marcar/desmarcar se
 * muestra como aviso. Presentacional + hooks; sin `fetch`.
 */
import { useParams, Link } from "react-router-dom";

import { Button, Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { ROUTES } from "@/features/navigation";

import { talonarioErrorMessage } from "../format";
import { useMarkNumber, useUnmarkNumber } from "../hooks/useMarkNumber";
import { useTalonarioGrid } from "../hooks/useTalonarioGrid";
import { RequireManageTalonarios } from "./RequireManageTalonarios";
import { TalonarioGridSummary } from "./TalonarioGridSummary";
import { TalonarioGridView } from "./TalonarioGridView";

export function TalonarioDetailScreen() {
  const { worker = "" } = useParams<{ worker: string }>();
  const grid = useTalonarioGrid(worker);
  const mark = useMarkNumber();
  const unmark = useUnmarkNumber();

  // Error de la acción en curso (marcar o desmarcar), si lo hubo.
  const actionError = mark.isError
    ? talonarioErrorMessage(mark.error)
    : unmark.isError
      ? talonarioErrorMessage(unmark.error)
      : null;

  // Número con una mutación en vuelo, para deshabilitar solo su botón.
  const pendingNumber =
    mark.isPending && mark.variables
      ? mark.variables.number
      : unmark.isPending && unmark.variables
        ? unmark.variables.number
        : null;

  return (
    <RequireManageTalonarios title="Detalle de talonario">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            Talonario de {worker}
          </h1>
          <Button asChild variant="ghost" size="md">
            <Link to={ROUTES.talonarios}>Volver a talonarios</Link>
          </Button>
        </div>

        {grid.isPending ? (
          <p className="text-sm text-slate-500" role="status">
            Cargando talonario…
          </p>
        ) : grid.isError ? (
          <p className="text-sm text-red-600" role="alert">
            {talonarioErrorMessage(grid.error)}
          </p>
        ) : (
          <>
            <TalonarioGridSummary grid={grid.data} />

            {actionError && (
              <p className="text-sm text-red-600" role="alert">
                {actionError}
              </p>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Rango {grid.data.range}</CardTitle>
              </CardHeader>
              <CardContent>
                <TalonarioGridView
                  numbers={grid.data.numbers}
                  onMark={(number) => mark.mutate({ worker, number })}
                  onUnmark={(number) => unmark.mutate({ worker, number })}
                  pendingNumber={pendingNumber}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </RequireManageTalonarios>
  );
}
